// Primary Flight Display (M32, AVI-2): a compact PFD shown while flying — an
// artificial-horizon canvas (pitch ladder + bank) plus airspeed / altitude /
// heading / vertical-speed / throttle readouts. Client-only (ENG-1); it reads the
// sim aircraft state each frame. Toggled on/off by the boot when you enter/leave a
// cockpit. Deliberately simple + readable (PLAN §4), not a certified instrument.
import { h } from '../h';
import { TICK } from '../../../sim/config';
import type { Aircraft } from '../../../sim/aviation/aircraft';

const W = 220, HH = 140;

/** Compass heading (0–360, 0 = north/−z) from the aircraft yaw. */
export function headingDeg(a: Aircraft): number {
  return ((a.yaw * 180) / Math.PI % 360 + 360) % 360;
}
/** Airspeed in blocks/second (≈ m/s) from the velocity magnitude. */
export function airspeed(a: Aircraft): number {
  return Math.hypot(a.vx, a.vy, a.vz) * TICK.TPS;
}

export class AviationHud {
  readonly el: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly text: HTMLElement;
  private shown = false;

  constructor() {
    this.canvas = h('canvas', { width: W, height: HH, style: { display: 'block', borderRadius: '4px' } });
    this.text = h('div', { class: 'bb-pfd-text', style: {
      font: '12px monospace', color: '#e8f0ff', marginTop: '4px', whiteSpace: 'pre', textShadow: '0 1px 2px #000',
    } });
    this.el = h('div', { class: 'bb-pfd', style: {
      position: 'absolute', left: '12px', bottom: '12px', padding: '6px',
      background: 'rgba(10,16,28,0.55)', borderRadius: '6px', pointerEvents: 'none', display: 'none',
    } }, this.canvas, this.text);
  }

  setVisible(v: boolean): void { this.shown = v; this.el.style.display = v ? 'block' : 'none'; }
  get visible(): boolean { return this.shown; }

  update(a: Aircraft): void {
    if (!this.shown) return;
    this.drawHorizon(a);
    const vsi = a.vy * TICK.TPS; // blocks/s
    this.text.textContent =
      `SPD ${airspeed(a).toFixed(0).padStart(3)}  ALT ${a.y.toFixed(0).padStart(4)}\n` +
      `HDG ${headingDeg(a).toFixed(0).padStart(3)}  VS ${(vsi >= 0 ? '+' : '') + vsi.toFixed(1)}\n` +
      `THR ${(a.throttle * 100).toFixed(0).padStart(3)}%  ${a.onGround ? 'GND' : 'AIR'}${a.crashed ? '  ⚠CRASH' : ''}`;
  }

  /** Artificial horizon: sky/ground split rotated by roll and shifted by pitch. */
  private drawHorizon(a: Aircraft): void {
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;
    ctx.save();
    ctx.clearRect(0, 0, W, HH);
    ctx.beginPath(); ctx.rect(0, 0, W, HH); ctx.clip();
    ctx.translate(W / 2, HH / 2);
    ctx.rotate(-a.roll);
    const horizonY = a.pitch * (HH * 0.9); // pitch pushes the horizon up/down
    ctx.fillStyle = '#3a78c8'; ctx.fillRect(-W, -HH + horizonY, W * 2, HH); // sky
    ctx.fillStyle = '#6b4a2a'; ctx.fillRect(-W, horizonY, W * 2, HH);       // ground
    ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(-W, horizonY); ctx.lineTo(W, horizonY); ctx.stroke();
    ctx.restore();
    // Fixed aircraft reference (wings + dot).
    ctx.strokeStyle = '#ffd54a'; ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(W / 2 - 30, HH / 2); ctx.lineTo(W / 2 - 8, HH / 2);
    ctx.moveTo(W / 2 + 8, HH / 2); ctx.lineTo(W / 2 + 30, HH / 2);
    ctx.stroke();
  }
}

// ── SAR instruments (M36.1, SAR-1..3): a top-centre compass ribbon + a coordinate
//    readout (MGRS + lat/lon in Atlas, else world XYZ) + ELT signal-strength bars.
//    Toggled with the map key. Client-only (ENG-1); the sim owns the RSSI/objective. ──
const CW = 260, CHH = 30;

/** Everything the SAR HUD draws each frame — boot computes it from the pure model. */
export interface SarReadout {
  /** Compass heading to true north (0–360°). */
  headingDeg: number;
  /** Coordinate readout lines (MGRS + lat/lon, or XYZ). */
  lines: string[];
  /** ELT signal bars 0..maxBars (0 = no signal / no beacon in range). */
  bars: number;
  maxBars: number;
  /** Horizontal distance to the homed beacon (blocks), or null if none in range. */
  dist: number | null;
  /** Dwell progress toward a find, 0..1. */
  dwellFrac: number;
  /** The searcher has scored a find. */
  found: boolean;
}

export class SarHud {
  readonly el: HTMLElement;
  private readonly ribbon: HTMLCanvasElement;
  private readonly text: HTMLElement;
  private shown = false;

  constructor() {
    this.ribbon = h('canvas', { width: CW, height: CHH, style: { display: 'block', borderRadius: '4px' } });
    this.text = h('div', { class: 'bb-sar-text', style: {
      font: '12px monospace', color: '#e8f0ff', marginTop: '4px', textAlign: 'center',
      whiteSpace: 'pre', textShadow: '0 1px 2px #000',
    } });
    this.el = h('div', { class: 'bb-sar', 'data-testid': 'sar-hud', style: {
      position: 'absolute', left: '50%', top: '10px', transform: 'translateX(-50%)', padding: '6px 10px',
      background: 'rgba(10,16,28,0.55)', borderRadius: '6px', pointerEvents: 'none', display: 'none',
    } }, this.ribbon, this.text);
  }

  setVisible(v: boolean): void { this.shown = v; this.el.style.display = v ? 'block' : 'none'; }
  toggle(): boolean { this.setVisible(!this.shown); return this.shown; }
  get visible(): boolean { return this.shown; }

  update(r: SarReadout): void {
    if (!this.shown) return;
    this.drawRibbon(r.headingDeg);
    const bar = r.bars > 0
      ? `ELT ${'▮'.repeat(r.bars)}${'▯'.repeat(r.maxBars - r.bars)}` + (r.dist !== null ? `  ~${r.dist.toFixed(0)}m` : '')
      : 'ELT  —— no signal';
    const status = r.found ? '  ✔ FOUND' : r.dwellFrac > 0 ? `  hold ${(r.dwellFrac * 100).toFixed(0)}%` : '';
    this.text.textContent = `${r.lines.join('\n')}\n${bar}${status}`;
  }

  /** A moving heading tape centred on the current heading (true north at 0/360). */
  private drawRibbon(headingDeg: number): void {
    const ctx = this.ribbon.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, CW, CHH);
    ctx.save();
    ctx.beginPath(); ctx.rect(0, 0, CW, CHH); ctx.clip();
    const pxPerDeg = 2;
    const cardinals: Record<number, string> = { 0: 'N', 45: 'NE', 90: 'E', 135: 'SE', 180: 'S', 225: 'SW', 270: 'W', 315: 'NW' };
    ctx.strokeStyle = '#8aa'; ctx.fillStyle = '#cfe'; ctx.font = '10px monospace'; ctx.textAlign = 'center';
    for (let d = -90; d <= 90; d += 15) {
      const deg = ((Math.round(headingDeg) + d) % 360 + 360) % 360;
      const x = CW / 2 + d * pxPerDeg;
      const tall = deg % 45 === 0;
      ctx.globalAlpha = 0.8; ctx.beginPath(); ctx.moveTo(x, 2); ctx.lineTo(x, tall ? 12 : 8); ctx.stroke();
      if (tall) ctx.fillText(cardinals[deg] ?? `${deg}`, x, 24);
    }
    ctx.restore();
    // Fixed centre pointer.
    ctx.fillStyle = '#ffd54a'; ctx.beginPath();
    ctx.moveTo(CW / 2, 0); ctx.lineTo(CW / 2 - 5, 8); ctx.lineTo(CW / 2 + 5, 8); ctx.closePath(); ctx.fill();
  }
}

// ── Becker DF dial (M36.2, SAR-4): the airborne direction finder — a heading-
//    indicator-style ring with a dot that rides it at the beacon's RELATIVE bearing
//    (dead-ahead = top). The dot is hidden when there is no signal; the host shows
//    the dial only while flying with a `becker_df` aboard. Client-only (ENG-1). ──
const BW = 120;

export class BeckerDial {
  readonly el: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private shown = false;

  constructor() {
    this.canvas = h('canvas', { width: BW, height: BW, style: { display: 'block' } });
    this.el = h('div', { class: 'bb-becker', 'data-testid': 'becker-df', style: {
      position: 'absolute', right: '12px', bottom: '12px', padding: '6px',
      background: 'rgba(10,16,28,0.55)', borderRadius: '8px', pointerEvents: 'none', display: 'none',
    } }, this.canvas);
  }

  setVisible(v: boolean): void { this.shown = v; this.el.style.display = v ? 'block' : 'none'; }
  get visible(): boolean { return this.shown; }

  /** `relBearing` in radians (0 = dead ahead), or null for no signal → dot hidden. */
  update(relBearing: number | null): void {
    if (!this.shown) return;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;
    const c = BW / 2, R = BW / 2 - 10;
    ctx.clearRect(0, 0, BW, BW);
    // Bezel + ticks.
    ctx.strokeStyle = '#8aa'; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(c, c, R, 0, Math.PI * 2); ctx.stroke();
    ctx.strokeStyle = '#456'; ctx.lineWidth = 1;
    for (let a = 0; a < 360; a += 30) {
      const t = (a * Math.PI) / 180;
      ctx.beginPath(); ctx.moveTo(c + Math.sin(t) * (R - 4), c - Math.cos(t) * (R - 4));
      ctx.lineTo(c + Math.sin(t) * R, c - Math.cos(t) * R); ctx.stroke();
    }
    // Fixed aircraft symbol at the top (dead-ahead reference).
    ctx.fillStyle = '#ffd54a'; ctx.beginPath();
    ctx.moveTo(c, 6); ctx.lineTo(c - 5, 16); ctx.lineTo(c + 5, 16); ctx.closePath(); ctx.fill();
    ctx.fillStyle = '#cfe'; ctx.font = '10px monospace'; ctx.textAlign = 'center'; ctx.fillText('DF', c, c + 3);
    // The bearing dot rides the ring at relBearing (0 = top, clockwise).
    if (relBearing !== null) {
      const dx = c + Math.sin(relBearing) * R, dy = c - Math.cos(relBearing) * R;
      ctx.fillStyle = '#37d67a'; ctx.beginPath(); ctx.arc(dx, dy, 6, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#37d67a'; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(c, c); ctx.lineTo(dx, dy); ctx.stroke();
    }
  }
}
